<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { usePage } from '@inertiajs/vue3'
import Modal from '@utilities/Modal.vue'
import ProductVariant from '@components/product/ProductVariant.vue'
import SlideCarousel from '@utilities/Carousel/SlideCarousel.vue'
import { fetchJson } from '@/fetch.js'
import GtmEvent from '@modules/GtmEvent.js'
import { bannerPromotionGrouping, bannerProductModalCreativeSlot } from '@modules/bannerGtm.js'

const props = defineProps({
    bannerId: {
        type: Number,
        required: true,
    },
    banner: {
        type: Object,
        default: null,
    },
    gtmCreativeSlot: {
        type: String,
        default: null,
    },
    initialHeading: {
        type: String,
        default: null,
    },
    initialCtaUrl: {
        type: String,
        default: null,
    },
    initialCtaText: {
        type: String,
        default: null,
    },
})

const emit = defineEmits(['close'])

const page = usePage()

const isLoading = ref(true)
const modalHeading = ref(props.initialHeading)
const ctaUrl = ref(props.initialCtaUrl)
const ctaText = ref(props.initialCtaText)
const products = ref([])

const productGrouping = computed(() => {
    const bannerForGrouping = props.banner || { banner_id: props.bannerId, label: modalHeading.value }

    return bannerPromotionGrouping(
        bannerForGrouping,
        bannerProductModalCreativeSlot(props.gtmCreativeSlot)
    )
})

const productGroupingAttribute = computed(() => {
    if (! productGrouping.value) {
        return '{}'
    }

    return JSON.stringify(productGrouping.value)
})

const trackModalProductsView = () => {
    if (! products.value.length || ! productGrouping.value) {
        return
    }

    new GtmEvent({
        eventName: 'view_promotion',
        items: products.value,
        valueRequired: false,
        crossSellArea: productGrouping.value,
        categoryListDetails: page.props.currentCategory || null,
    }).trackGtmEvent()
}

const loadProducts = async () => {
    try {
        const response = await fetchJson({
            url: `/api/banners/${props.bannerId}/modal-products`,
        })

        if (! response.ok) {
            emit('close')
            return
        }

        const data = await response.json()
        modalHeading.value = data.modal_heading ?? props.initialHeading
        ctaUrl.value = data.cta_url ?? props.initialCtaUrl
        ctaText.value = data.cta_text ?? props.initialCtaText
        products.value = data.products ?? []

        if (! products.value.length) {
            if (ctaUrl.value) {
                window.location.href = ctaUrl.value
                return
            }

            emit('close')
            return
        }

        isLoading.value = false
        await nextTick()
        trackModalProductsView()
    } catch (error) {
        emit('close')
        isLoading.value = false
    }
}

onMounted(() => {
    loadProducts()
})
</script>

<template>
    <Modal
        width="w-full sm:max-w-4xl"
        @modalClose="emit('close')"
    >
        <div class="bg-white w-full max-h-[90vh]">
            <div class="flex flex-col p-4 relative">
                <button
                    type="button"
                    @click="emit('close')"
                    class="transition-300 ml-2 h-10 w-10 text-red-600 absolute top-3 right-3 rounded-full border-2 p-0"
                >
                    <fa-icon
                        icon="fa fa-times"
                        title="Close"
                    />
                </button>

                <h2
                    v-if="modalHeading"
                    class="text-xl md:text-2xl font-bold font-heading text-center pr-10"
                >
                    {{ modalHeading }}
                </h2>

                <div
                    v-if="isLoading"
                    class="flex justify-center items-center min-h-[200px]"
                >
                    <fa-icon
                        icon="fa fa-spinner"
                        class="fa-spin text-2xl text-brand-600"
                    />
                </div>

                <template v-else>
                    <div
                        class="product-grouping"
                        :data-product-grouping="productGroupingAttribute"
                    >
                        <SlideCarousel
                            :items="products"
                            :slides-per-view="1"
                            :slides-per-view-md="3"
                            gap="0.1rem"
                        >
                            <template #default="{ item: product }">
                                <!-- vertical: portrait cards on all breakpoints in the modal carousel -->
                                <ProductVariant
                                    :variant="product"
                                    vertical
                                />
                            </template>
                        </SlideCarousel>
                    </div>

                    <div
                        v-if="ctaUrl && ctaText"
                        class="flex justify-center"
                    >
                        <a
                            :href="ctaUrl"
                            class="btn btn__submit"
                        >
                            {{ ctaText }}
                        </a>
                    </div>
                </template>
            </div>
        </div>
    </Modal>
</template>
